import { BaseAction } from "../../../core/actions/BaseAction";
import { isLiveChangeDateCheckout } from "../../../Lib/Utils/changeDate";
import { determinePassengerType, isEmpty } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AncillaryStatusEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IDeterminePassenger, IUserResponse } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IPreparePassengerForAddons } from "../../../Orchestrator/Types/Interface/ActionParams/GetAddonsParams";
import {
    IAddonsPassenger,
    IAddonsPassengerLeg
} from "../../../Orchestrator/Types/Interface/IAddOns";
import { ConfirmAddonsAction } from "./ConfirmAddonsAction";
import { persistAddonsOnConfirmAction } from "./PersistAddonsOnConfirmAction";

export class PreparePassengerForAddons extends BaseAction<
    IDefaultActionResult,
    IPreparePassengerForAddons
> {
    override async run(): Promise<IDefaultActionResult | null> {
        const actionResult: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail",
        };
        try {
            const { passengers, selectedTrainLegs, passengerTypes, alreadyAddonsLegPassenger, meal, paymentSummary, bookedTrips, isHyperFast = false } = this.params!;
            // `manageSeatingBaselineAddonsLegPassengerAtom` is populated when
            // the manage-seating flow starts and only cleared by
            // `ResetManageSeatingStateAction`. A non-empty baseline is a
            // reliable signal that we're in manage-seating mode — use it
            // instead of reading `isManageSeatingAtom` directly so this path
            // survives any future changes to flow-mode atom lifecycles.
            const manageSeatingBaseline = getRecoilStateValue<object | null>("manageSeatingBaselineAddonsLegPassengerAtom");
            const isInManageSeating = !isEmpty(manageSeatingBaseline);
            // Change-date carry-over: when the original booking had
            // ancillaries, SyncAlreadyAddonsAction (gated on the same
            // `isChangeDateFlow` localStorage marker) seeds
            // `alreadyAddonsLegPassengerAtom` with the REFUNDED-on-EXCHANGED
            // set. Pre-select those equivalents in the new prebooked offer
            // so the user starts from "what I had before" instead of an
            // empty modal. The stopCode-based matcher below already crosses
            // the old→new legId boundary, so no additional translation is
            // required here.
            const isChangeDateFlow = isLiveChangeDateCheckout() && !isInManageSeating;
            const shouldPreselect = isHyperFast || isInManageSeating || isChangeDateFlow;
            const addonsPassenger: IAddonsPassenger[] = [];
            const loggedUserDetails: Partial<IUserResponse> = getRecoilStateValue("userDataAtom") || {}
            let isActivePassengerSet = false;
            passengers.forEach((passenger) => {
                const determinePassenger: IDeterminePassenger = {
                    age: passenger.age || 0,
                    cards: passenger?.cards || [],
                    prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                }
                const passengerType = determinePassengerType(determinePassenger)
                //Skip Infants
                if (passengerType === "infants") {
                    return;
                }
                const passengerShortDescription = passengerTypes.find(d => d.id === passengerType)?.shortDescription || "";
                const passengerValue = passengerTypes?.find(d => d.id === passengerType)?.value || "";
                addonsPassenger.push({
                    passengerId: passenger.id,
                    passengerRef: passenger.externalRef,
                    passengerName: `${passenger?.detail?.firstName || ""} ${passenger?.detail?.lastName || ""
                        }`,
                    isActive: !isActivePassengerSet,
                    addonItem: {},
                    shortDescription: passengerShortDescription,
                    passengerValue: passengerValue,
                    passengerType: passengerType,
                    isMePassenger: (loggedUserDetails?.firstName === passenger?.detail?.firstName && loggedUserDetails?.lastName === passenger?.detail?.lastName)
                });
                isActivePassengerSet = true;
            });                        
            const outboundLegs = (selectedTrainLegs?.outbound || []).map(d => {
                return {
                    ...d,
                    direction: "outbound"
                }
            })
            const inboundLegs = (selectedTrainLegs?.inbound || []).map(d => {
                return {
                    ...d,
                    direction: "inbound"
                }
            })
            const allLegs = [...outboundLegs || [], ...inboundLegs || []]
            const prepareLegAddons: IAddonsPassengerLeg[] = []
            let needToUpdateConfirmedAddons = false
            allLegs.forEach(leg => {
                const addonsPassengerLeg: IAddonsPassengerLeg = {
                    legId: leg.legId,
                    addonsPassenger: addonsPassenger,
                };
                if (shouldPreselect && !isEmpty(alreadyAddonsLegPassenger)) {
                    const newLgAddons = meal[leg.legId]
                    let passengerWithAlreadyAddons: any = []
                    const alreadyAddons = alreadyAddonsLegPassenger[leg.direction]
                    alreadyAddons?.forEach(legAddons => {
                        let alreadyAddonItem: any = {}
                        const stopCodeNewLeg = `${leg.origin.stopPlaceRef}-${leg.destination.stopPlaceRef}`
                        if (stopCodeNewLeg === legAddons.stopCode) {
                            const legCategory = Object.keys(newLgAddons).find(category => category === legAddons.category)
                            if (legCategory) {
                                const oldAddonsExitsInNewAddons = newLgAddons[legCategory].find(d => d.type === legAddons.type)
                                if (oldAddonsExitsInNewAddons) {
                                    // Mark as not-new so confirm flow won't POST duplicates
                                    // Stamp isPrebooked so the Extras summary in
                                    // ManageSeatingAndAddons can render the "NEW" tag via a
                                    // single positive rule: only PREBOOKED ancillaries
                                    // (which will be re-POSTed on submit) are flagged.
                                    // FULFILLED / REFUNDED items stay untagged.
                                    alreadyAddonItem[legAddons.category] = {
                                        ...oldAddonsExitsInNewAddons,
                                        isNew: false,
                                        isPrebooked: legAddons.status === AncillaryStatusEnum.PREBOOKED,
                                    } as any
                                    if (passengerWithAlreadyAddons.find((d: any) => d.passengerExternalRef === legAddons.passengerRef)) {
                                        passengerWithAlreadyAddons = passengerWithAlreadyAddons.map((d: any) => {
                                            if (d.passengerExternalRef === legAddons.passengerRef) {
                                                d.alreadyAddonItem = {
                                                    ...d.alreadyAddonItem || {},
                                                    ...alreadyAddonItem
                                                }
                                            }
                                            return d
                                        })
                                    }
                                    else {
                                        // Stamp isPrebooked so the Extras summary in
                                    // ManageSeatingAndAddons can render the "NEW" tag via a
                                    // single positive rule: only PREBOOKED ancillaries
                                    // (which will be re-POSTed on submit) are flagged.
                                    // FULFILLED / REFUNDED items stay untagged.
                                    alreadyAddonItem[legAddons.category] = {
                                        ...oldAddonsExitsInNewAddons,
                                        isNew: false,
                                        isPrebooked: legAddons.status === AncillaryStatusEnum.PREBOOKED,
                                    } as any
                                        passengerWithAlreadyAddons.push({ passengerExternalRef: legAddons.passengerRef, alreadyAddonItem });
                                    }
                                }
                            }
                        }
                    })
                    const modifiedAddonsPassenger: IAddonsPassenger[] = []
                    if (addonsPassenger.length > 0) {
                        addonsPassenger.forEach(passenger => {
                            passenger.addonItem = passengerWithAlreadyAddons.find((d: any) => d.passengerExternalRef === passenger.passengerRef)?.alreadyAddonItem || {}
                            modifiedAddonsPassenger.push({ ...passenger })
                        })
                    }
                    if (modifiedAddonsPassenger.length > 0) {
                        addonsPassengerLeg.addonsPassenger = modifiedAddonsPassenger
                        needToUpdateConfirmedAddons = true
                    }
                }
                prepareLegAddons.push(addonsPassengerLeg)
            })

            // Restore previously confirmed add-ons
            const confirmedAddons: IAddonsPassengerLeg[] = getRecoilStateValue("confirmAddonsPassengerListAtom") || [];
            if (confirmedAddons.length > 0) {
                prepareLegAddons.forEach(leg => {
                    const confirmed = confirmedAddons.find(c => c.legId === leg.legId);
                    if (!confirmed) return;
                    leg.addonsPassenger = leg.addonsPassenger.map(passenger => {
                        const confirmedPassenger = confirmed.addonsPassenger.find(
                            cp => cp.passengerId === passenger.passengerId
                        );
                        if (!confirmedPassenger || Object.keys(confirmedPassenger.addonItem).length === 0) {
                            return passenger;
                        }
                        return { ...passenger, addonItem: { ...confirmedPassenger.addonItem } };
                    });
                });
            }

            updateRecoilState("addonsPassengerListAtom", prepareLegAddons);
            if (needToUpdateConfirmedAddons) {
                const confirmAddonsAction = new ConfirmAddonsAction();
                confirmAddonsAction.setParams({
                    addonsPassengerList: prepareLegAddons,
                    paymentSummary: paymentSummary,
                    bookedTrips: bookedTrips,
                    confirmAddonsPassengerList: [],
                    isFromPrepare: "prepareAddons"
                });
                await confirmAddonsAction.execute();

                // Change-date auto-POST: now that ConfirmAddonsAction has
                // captured the carry-over preselect into
                // confirmAddonsPassengerListAtom, immediately POST the
                // equivalents to the new prebooked offer so they are owned
                // by the new booking — without requiring the user to open
                // the Extras modal. Idempotent against re-fires:
                // PersistAddonsOnConfirmAction's W-guard skips a POST when
                // an equivalent PREBOOKED ancillary already exists, and the
                // X-guard locks identical concurrent POSTs by request key.
                // Strictly gated on the live change-date marker so other
                // flows (regular checkout, manage-seating, upgrade) don't
                // pick up an auto-POST they never asked for.
                if (isChangeDateFlow) {
                    const scopeLegIds = prepareLegAddons
                        .map((l) => l?.legId)
                        .filter((id): id is string => Boolean(id));
                    if (scopeLegIds.length > 0) {
                        persistAddonsOnConfirmAction.setParams({ scopeLegIds });
                        await persistAddonsOnConfirmAction.execute();
                    }
                }
            }

            actionResult.status = "success";
        } catch (error: any) {
            console.log("error : ", error.message)
            actionResult.message = error.message;
        } finally {
            return actionResult;
        }
    }
}

export const preparePassengerForAddons = new PreparePassengerForAddons();
